/**
 * AI Bot Framework Types - New explicit session-based architecture
 */

// Core message types
export interface BotMessage {
  sessionId: string;
  requestId: string;
  botId: string;
  botName: string;
  messageType: BotMessageType;
  content?: string;
  metadata: Record<string, any>;
  isComplete: boolean;
  isError: boolean;
  errorMessage?: string;
  timestamp: string;
  tokenUsage?: TokenUsage;
}

export enum BotMessageType {
  TextMessage = 'TextMessage',
  FormPreview = 'FormPreview',
  AnalyticsData = 'AnalyticsData',
  DataExtraction = 'DataExtraction',
  ToolInvocation = 'ToolInvocation',
  ToolComplete = 'ToolComplete',
  BotSwitch = 'BotSwitch',
  StatusUpdate = 'StatusUpdate',
  Error = 'Error',
  Welcome = 'Welcome',
  System = 'System'
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  model: string;
  estimatedCost?: number;
}

// Session management types
export interface RegisterBotSessionRequest {
  sessionId: string;
  requestedBotId: string;
  sessionType: SessionType;
  pageContext: BotPageContext;
  initialMessage?: string;
  // Note: TenantId and UserId are always derived from auth context on server
}

export interface SendBotMessageRequest {
  sessionId: string;
  content: string;
  requestId: string;
  metadata?: Record<string, any>;
  pageContext?: BotPageContext;
}

export interface BotSessionEstablishedResponse {
  sessionId: string;
  assignedBot: BotInfo;
  requestedBotAvailable: boolean;
  fallbackReason?: string;
}

export interface BotSwitchedNotification {
  sessionId: string;
  previousBot: BotInfo;
  newBot: BotInfo;
  reason: string;
}

export enum SessionType {
  Visible = 'Visible',
  Background = 'Background'
}

export interface BotPageContext {
  page: string;
  metadata: Record<string, any>;
  url?: string;
}

export interface BotInfo {
  botId: string;
  botName: string;
  description: string;
  capabilities: string[];
  version: string;
}

// Hook configuration
export interface AIBotConfig {
  requestedBotId: string;
  pageContext?: BotPageContext; // Legacy - use pageContextRef instead
  pageContextRef?: React.MutableRefObject<BotPageContext>; // Preferred - always fresh
  sessionType: SessionType;
  onMessage?: (message: BotMessage) => void;
  initialMessage?: string;
  // Note: TenantId and UserId are always derived from auth context on server
}

// Hook return type
export interface AIBotSession {
  sessionId: string;
  isConnected: boolean;
  isRegistered: boolean;
  currentBot: BotInfo | null;
  availableBots: BotInfo[];
  messages: BotMessage[];
  isProcessing: boolean;
  error: string | null;

  // Actions
  sendMessage: (content: string, metadata?: Record<string, any>) => Promise<void>;
  switchBot: (newBotId: string) => Promise<void>;
  registerSession: () => Promise<void>;
  unregisterSession: () => Promise<void>;
  clearMessages: () => void;

  // Connection management
  reconnect: () => Promise<void>;
}

// SignalR client interface
export interface AIBotClient {
  ReceiveBotMessage: (message: BotMessage) => void;
  ReceiveActionResponse: (message: BotMessage) => void;
  BotSessionEstablished: (response: BotSessionEstablishedResponse) => void;
  BotSwitched: (notification: BotSwitchedNotification) => void;
  SessionTerminated: (sessionId: string, reason: string) => void;
  AvailableBotsUpdated: (data: AvailableBotsUpdatedEvent) => void;
}

// Available bots event
export interface AvailableBotsUpdatedEvent {
  sessionId: string;
  availableBots: BotInfo[];
}

// Chat UI types for components
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  botName?: string;
  isMarkdown?: boolean;
  isProcessing?: boolean;
  error?: string;
}

// Component configuration
export interface VisibleChatConfig extends AIBotConfig {
  sessionType: SessionType.Visible;
  placeholder?: string;
  title?: string;
  showBotInfo?: boolean;
  maxHeight?: string;
  className?: string;
}

export interface BackgroundAIConfig extends AIBotConfig {
  sessionType: SessionType.Background;
  autoStart?: boolean;
}

// Message interpretation helpers
export type MessageHandler = (message: BotMessage) => void;

export interface MessageTypeHandlers {
  [BotMessageType.TextMessage]?: MessageHandler;
  [BotMessageType.FormPreview]?: MessageHandler;
  [BotMessageType.AnalyticsData]?: MessageHandler;
  [BotMessageType.ToolInvocation]?: MessageHandler;
  [BotMessageType.ToolComplete]?: MessageHandler;
  [BotMessageType.BotSwitch]?: MessageHandler;
  [BotMessageType.StatusUpdate]?: MessageHandler;
  [BotMessageType.Error]?: MessageHandler;
  [BotMessageType.Welcome]?: MessageHandler;
  [BotMessageType.System]?: MessageHandler;
}

// Error types
export interface AIBotError {
  code: string;
  message: string;
  sessionId?: string;
  requestId?: string;
  timestamp: string;
}

// Debug information
export interface SessionDebugInfo {
  sessionId: string;
  connectionId?: string;
  registeredAt?: string;
  lastActivity?: string;
  messageCount: number;
  requestedBot: string;
  assignedBot?: string;
  sessionType: SessionType;
  pageContext: BotPageContext;
}